import React, { useState } from 'react';
import type { MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import { useSwipeable, type SwipeableHandlers } from 'react-swipeable';

import { Badge } from 'apps/auth/components/common/Badge/Badge';
import clsx from 'clsx';

import styles from './Panel.module.css';

type PanelType = 'div' | 'link' | 'reactLink' | 'button' | 'submit';
type PanelJustify = 'start' | 'center' | 'end' | 'between';

const componentMap: Record<PanelType, React.ElementType> = {
    div: 'div',
    link: 'a',
    reactLink: Link,
    button: 'button',
    submit: 'button',
};

const justifyClass: Record<PanelJustify, string> = {
    start: styles.justifyStart,
    center: styles.justifyCenter,
    end: styles.justifyEnd,
    between: styles.justifyBetween,
};

interface PanelProps {
    type?: PanelType;
    isHoverable?: boolean;
    isBordered?: boolean;
    hasBorderRadius?: boolean;
    hasIcon?: boolean;
    justify?: PanelJustify;
    href?: string;
    to?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    negativeMargin?: boolean;
    id?: string;
    target?: string;
    isDisabled?: boolean;
    isDefaultCursor?: boolean;
    dataTestId?: string;
    isSelectedPanel?: boolean;
    isSelectedBordered?: boolean;
    badgeCaption?: string;
    children?: React.ReactNode;
}

export const Panel: React.FC<PanelProps> = ({
    type = 'div',
    isHoverable,
    isBordered,
    hasBorderRadius,
    hasIcon,
    justify,
    href,
    to,
    onClick,
    negativeMargin,
    id,
    target,
    isDisabled,
    isDefaultCursor,
    dataTestId,
    isSelectedPanel,
    isSelectedBordered,
    badgeCaption,
    children,
}) => {
    const Wrapper = componentMap[type] ?? 'div';
    const [showDeleteIcon, setShowDeleteIcon] = useState(false);

    const handlers: SwipeableHandlers = useSwipeable({
        onSwipedLeft: () => setShowDeleteIcon(true),
        onSwipedRight: () => setShowDeleteIcon(false),
        preventScrollOnSwipe: true,
        trackTouch: true,
    });

    return (
        <Wrapper
            data-testid={dataTestId}
            className={clsx(
                styles.panel,
                justify && justifyClass[justify],
                isDefaultCursor && styles.defaultCursor,
                isBordered && styles.bordered,
                hasIcon && styles.hasIcon,
                hasBorderRadius && styles.hasBorderRadius,
                negativeMargin && styles.negativeMargin,
                isDisabled && styles.disabled,
                isHoverable && styles.hoverable,
                isSelectedPanel && styles.selected,
                isSelectedBordered && styles.selectedBordered,
                showDeleteIcon && styles.showDeleteIcon,
            )}
            href={href}
            to={to}
            onClick={onClick}
            id={id}
            target={target}
            type={type === 'button' || type === 'submit' ? type : undefined}
            {...handlers}
        >
            {badgeCaption && (
                <div className={styles.badge}>
                    <Badge variant="primary" caption={badgeCaption} />
                </div>
            )}
            {children}
        </Wrapper>
    );
};
